import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
export type SelectDropdownOption = {
  value: string;
  label: string;
};
export interface SelectDropdownProps {
  value: string;
  options: SelectDropdownOption[];
  onChange: (value: string) => void;
  ariaLabel: string;
  size?: 'sm' | 'md';
  fullWidth?: boolean;
  menuAlign?: 'left' | 'right';
  icon?: React.ReactNode;
  scrollMenu?: boolean;
}
export function SelectDropdown({
  value,
  options,
  onChange,
  ariaLabel,
  size = 'md',
  fullWidth = false,
  menuAlign = 'left',
  icon,
  scrollMenu = false
}: SelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedOption = options.find(option => option.value === value);
  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);
  const triggerSizeClass = size === 'sm' ? 'px-3 py-1.5 text-sm' : 'px-4 py-2 text-sm';
  return <div ref={containerRef} className={`relative ${fullWidth ? 'w-full' : 'inline-block'}`} data-id="element-1584" data-mp="3aoylt8kodpw0">
      <button type="button" onClick={() => setIsOpen(open => !open)} aria-haspopup="listbox" aria-expanded={isOpen} aria-label={ariaLabel} className={`flex ${fullWidth ? 'w-full' : 'min-w-[8.5rem]'} items-center justify-between gap-2 rounded-lg border bg-white ${triggerSizeClass} font-medium text-gray-900 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#30718D] ${isOpen ? 'border-[#30718D]' : 'border-gray-300'}`} data-id="element-1585" data-mp="35xyqg7r7ffvm">
        <span className="flex min-w-0 items-center gap-2" data-id="element-1586" data-mp="1wou3qim10n7d">
          {icon && <span className="flex-none text-gray-400" aria-hidden="true" data-id="element-1587" data-mp="1i1iuwoh8bhay">
              {icon}
            </span>}
          <span className="truncate text-left" data-id="element-1588" data-mp="1myrdgq6ahncq">
            {selectedOption?.label || 'Select'}
          </span>
        </span>
        <ChevronDown size={16} aria-hidden="true" className={`flex-none text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-1589" data-mp="18yzv6d8qpksx" />
      </button>

      {isOpen && <div role="listbox" aria-label={ariaLabel} className={`absolute top-full z-30 mt-2 ${fullWidth ? 'w-full' : 'min-w-full'} rounded-lg border border-gray-200 bg-white shadow-lg ${scrollMenu ? 'max-h-72 overflow-y-auto' : 'overflow-hidden'} ${menuAlign === 'right' ? 'right-0' : 'left-0'}`} data-id="element-1590" data-mp="z4hnyy44774d">
          {options.map((option, index) => <button key={option.value} type="button" role="option" aria-selected={option.value === value} onClick={() => {
        onChange(option.value);
        setIsOpen(false);
      }} className={`w-full px-4 py-2.5 text-left text-sm transition-colors ${index < options.length - 1 ? 'border-b border-gray-100' : ''} ${option.value === value ? 'bg-[#30718D]/5 font-semibold text-[#30718D]' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-1591" data-mp="ms5jgsb6nqx0" data-mpi={index}>
              {option.label}
            </button>)}
        </div>}
    </div>;
}